import { ref, toRefs, watch } from 'vue';
import i18n from '@/i18n';
import { useLastValue, useCommonChartOptions } from '../useChart';
import { scpmUtil } from '@/utils/scpmUtil.js';
import { computed } from '@vue/reactivity';

const $t = (text) => {
  return i18n.global.t(text);
};
const { getPositionTooltip, getColorOfChart } = scpmUtil;

const useState = (state) => state.value;

export const AGGREGATION_LINE = {
  ID: '__aggregation__',
  NAME: `__aggregation__${Math.random()}`
};

export const ON_EVENTS = {
  UPDATE: 'on-update',
  CHANGE: 'on-change',
  INDIVIDUAL: 'on-indivItem',
  INTERVAL: 'on-interval',
  STATISTIC: 'on-statistic',
  ZOOM: 'on-zoom',
  RESET: 'on-reset',
  CLOSE: 'on-close',
  ADD_WIDGET: 'add-widget',
  ADD_EVENT_POLICY: 'add-event-policy'
};

export const useEventTracker = () => {
  const eventKeys = ref({});
  const isLatestEvent = (eventName, eventKey) => useState(eventKeys)[eventName] === eventKey;
  const getEventKey = (eventName) => useState(eventKeys)[eventName];
  const setEventKey = (eventName) => {
    const randomString = Math.random().toString(36).substring(2);
    const eventKey = [randomString, new Date().getTime()].join('_');
    eventKeys.value[eventName] = eventKey;
    return eventKey;
  };
  const resetEvents = () => {
    eventKeys.value = {};
  };
  return { isLatestEvent, getEventKey, setEventKey, resetEvents };
};

export const useChartType = () => {
  const chartType = ref({ type: 'line', stack: null, iconName: 'line_chart', iconType: 'line' });
  const chartTypeOptions = ref([
    {
      txt: 'Line',
      iconType: 'line',
      iconName: 'line_chart',
      type: 'line',
      stack: null,
      chartExopt: {},
      itemStyle: {
        cursor: 'crosshair'
      }
    },
    {
      txt: 'Stacked Area',
      iconType: 'line',
      iconName: 'stack_chart',
      type: 'line',
      stack: 'all',
      chartExopt: { areaStyle: {} },
      itemStyle: {
        cursor: 'crosshair'
      }
    },
    {
      txt: 'Scatter',
      iconType: 'line',
      iconName: 'scatter_chart',
      type: 'scatter',
      stack: null,
      chartExopt: { symbol: 'circle' },
      itemStyle: {
        cursor: 'crosshair'
      }
    }
  ]);
  const setChartType = ({ type, stack, iconName, iconType }) => {
    chartType.value = { type, stack, iconName, iconType };
  };

  const setDefaultChartType = () => {
    const { type, stack, iconName, iconType } = useState(chartTypeOptions)[0];
    chartType.value = { type, stack, iconName, iconType };
  };

  const isDefaultChartType = computed(() => {
    const { type: defaultType, stack: defaultStack } = useState(chartTypeOptions)[0];
    const { type: curType, stack: curStack } = useState(chartType);
    return curType === defaultType && curStack === defaultStack;
  });

  return { chartType, chartTypeOptions, setChartType, setDefaultChartType, isDefaultChartType };
};

export const useDownload = () => {
  const download = ref(null);
  const downloadOptions = ref([
    { txt: 'Chart PNG File', type: 'image' },
    { txt: 'Chart Excel File', type: 'excel' },
    { txt: 'Raw Excel File', type: 'excel-raw' }
  ]);
  return { download, downloadOptions };
};

const getChartOptionsDefault = (displayUnit) => {
  return {
    grid: [
      {
        height: 120,
        top: 30,
        bottom: 30,
        left: 16,
        right: 16,
        containLabel: true,
        tooltip: { show: true }
      },
      {
        show: false,
        top: 192,
        left: 20,
        right: 14,
        height: 26,
        borderColor: null,
        tooltip: { show: false }
      }
    ],
    legend: {
      id: 0,
      show: true,
      left: 0,
      top: 150,
      icon: 'circle',
      type: 'scroll',
      itemHeight: 8,
      itemWidth: 8,
      textStyle: { fontSize: 12, color: '#555B68' }
    },
    series: [],
    xAxis: [
      {
        gridIndex: 0,
        type: 'time',
        min: null,
        max: null,
        axisLabel: { interval: 'auto', fontSize: 12, formatter: null },
        nameLocation: 'center',
        splitNumber: 5
      },
      {
        gridIndex: 1,
        type: 'time',
        min: null,
        max: null,
        show: false
      }
    ],
    yAxis: [
      {
        gridIndex: 0,
        type: 'value',
        name: displayUnit,
        show: true,
        min: 0,
        max: 1,
        interval: 0.5,
        axisLabel: { formatter: null },
        axisLine: { show: true }
      },
      {
        gridIndex: 1,
        type: 'value',
        show: false,
        min: 0 - 10 / 100,
        max: 1 + 10 / 100,
        splitLine: { show: false }
      }
    ],
    toolbox: [
      {
        gridIndex: 0,
        feature: {
          dataZoom: {
            xAxisIndex: 0,
            yAxisIndex: 'none',
            title: { zoom: '', back: '' }
          }
        },
        top: -25
      }
    ],
    dataZoom: [
      {
        type: 'inside',
        start: 0,
        end: 100,
        bottom: 10,
        height: 26,
        handleSize: '80%',
        left: 16,
        right: 16
      },
      {
        type: 'slider',
        left: 16,
        right: 16,
        start: 0,
        end: 100,
        bottom: 8,
        height: 26,
        handleSize: '80%',
        show: false,
        showDetail: false,
        showDataShadow: false
      }
    ],
    tooltip: {
      backgroundColor: '#fff',
      textStyle: { color: '#000', fontSize: 12 },
      trigger: 'axis',
      axisPointer: { type: 'line' },
      confine: false,
      renderMode: 'html',
      className: 'toolbar-scrollbar',
      extraCssText: `
          max-height: 300px;
          overflow: auto;
          max-width: 600px;
          padding: 12px;
          display: flex;
          flex-direction: column;
          gap: 8px;
          opacity: 1;
          transition: opacity 0.4s ease;
        `,
      triggerOn: 'mousemove',
      enterable: true,
      transitionDuration: 0,
      appendToBody: true,
      valueFormatter: null,
      position: (point, _params, dom, rect, size) => {
        const position = getPositionTooltip(point, size);
        let top = position[1];
        let left = position[0];
        if (size.contentSize[0] > 800) left = position[0] / 4;
        if (size.contentSize[1] > 80) top = position[1];
        return [left, top];
      }
    },
    axisPointer: {
      snap: true,
      lineStyle: { type: 'dashed' },
      label: { show: true, margin: 6, backgroundColor: '#556', color: '#fff' },
      link: [{ xAxisId: ['chart1', 'chart2'] }, { xAxisId: ['chart2', 'chart1'] }]
    },
    animation: true,
    animationThreshold: 2000,
    animationEasingUpdate: 'cubicOut',
    animationDurationUpdate: 1500,
    progressive: 500,
    progressiveThreshold: 2000
  };
};

export const useChartOptions = (
  chartSeries,
  chartType,
  {
    lineColor,
    metricUnit,
    displayUnit,
    isEventDisabled,
    displayTime,
    originalInterval,
    displayInterval
  },
  props
) => {
  const { lastSeries, lastValueArr, updateLastValue } = useLastValue();
  const { showTooltipOfChart } = toRefs(props);
  const {
    setLegendDisplay,
    setActivedLegend,
    setYAxisMinMax,
    setDefaultYaxis,
    setXAxisFormatter,
    setXAxisMinMax,
    getYminMax,
    aggregateData,
    convertUnit,
    unitConfig,
    chartOptions,
    setLegendStatus,
    updateChartGrid,
    updateChartLegend,
    setEmptyChart,
    setZoomScale,
    setZoomBar,
    setSeries,
    setEnterable,
    setShowTooltip,
    setAnimation,
    downloadExcelChart,
    setGridView
  } = useCommonChartOptions(
    getChartOptionsDefault(displayUnit),
    chartType,
    metricUnit,
    displayUnit
  );

  const getChartTypeOptions = (idx, lstColor) => {
    const { type, stack } = useState(chartType);
    return {
      type,
      stack,
      cursor: 'crosshair',
      lineStyle: { color: lineColor[idx] ? lineColor[idx] : lstColor[idx], width: 1 },
      itemStyle: { color: lineColor[idx] ? lineColor[idx] : lstColor[idx] },
      symbolSize: type === 'scatter' ? 5 : 1,
      areaStyle: stack === 'all' ? {} : null
    };
  };

  const getAggLine = ({ chartData, chartType, displayTime, originalInterval, displayInterval }) => {
    return [
      {
        type: 'line',
        id: AGGREGATION_LINE.ID,
        name: AGGREGATION_LINE.NAME,
        animation: false,
        symbolSize: 0.5,
        itemStyle: { color: '#B0C4DE' },
        lineStyle: { color: '#B0C4DE', width: 1 },
        areaStyle: { color: '#B0C4DE', stroke: true, opacity: 0.1 },
        data: aggregateData({
          chartData,
          chartType,
          displayTime,
          originalInterval,
          displayInterval
        })
      }
    ];
  };

  watch(
    [() => chartSeries, () => chartType],
    () => {
      if (useState(isEventDisabled)) return;
      const displayData = useState(chartSeries);
      let lstColor = getColorOfChart(displayData.length);
      const seriesData = displayData.map((el, idx) => {
        // const { data, leftRemain = [], rightRemain = [] } = el;
        return {
          ...el,
          // data: [...leftRemain, ...data, ...rightRemain],
          ...getChartTypeOptions(idx, lstColor)
        };
      });
      const { max: refValue } = getYminMax(displayData, useState(chartType));
      const { data } = convertUnit(seriesData, refValue, metricUnit);
      setYAxisMinMax(displayData);
      setEnterable(displayData);
      setActivedLegend(displayData, [AGGREGATION_LINE.ID]);
      setShowTooltip(showTooltipOfChart.value);
      updateLastValue(data);
      setSeries(lastSeries.value, 0);
      //
      const aggLine = getAggLine({
        chartData: data,
        chartType: useState(chartType),
        displayTime: useState(displayTime),
        originalInterval: useState(originalInterval),
        displayInterval: useState(displayInterval)
      });
      setSeries(aggLine, 1);
    },
    { deep: true }
  );

  watch(
    () => showTooltipOfChart.value,
    (value) => setShowTooltip(value)
  );

  return {
    chartOptions,
    setGridView,
    setLegendDisplay,
    setXAxisMinMax,
    setXAxisFormatter,
    setDefaultYaxis,
    setYAxisMinMax,
    setZoomBar,
    setZoomScale,
    setEmptyChart,
    setLegendStatus,
    lastValueArr,
    updateChartLegend,
    updateChartGrid,
    unitConfig,
    setAnimation,
    downloadExcelChart
  };
};

export const useTargetMapping = (originalData) => {
  const representedTarget = ref({});
  const getMaxValues = (elKey) => {
    const maxValues = {};
    const filtered = useState(originalData).filter(({ key }) => key === elKey);
    for (const { data } of filtered) {
      for (const [xVal, yVal] of data) {
        maxValues[xVal] = Math.max(maxValues[xVal] || -Infinity, yVal);
      }
    }
    const maxValArr = [];
    for (const [key, value] of Object.entries(maxValues)) {
      maxValArr.push([Number(key), value]);
    }
    maxValArr.sort((a, b) => a[0] - b[0]);
    return maxValArr;
  };

  const getRepresentedTarget = () => {
    const representation = {};
    const oriData = useState(originalData);
    for (const { key: elKey } of oriData) {
      if (representation[elKey]) continue;
      representation[elKey] = getMaxValues(elKey);
    }
    representedTarget.value = representation;
  };

  const targetMapping = (displayTarget) => {
    const { key: disKey, statisticsType: disStatType } = displayTarget;
    const originalTarget = useState(originalData).find(
      ({ key, statisticsType }) => key === disKey && statisticsType === disStatType
    );
    if (originalTarget) return originalTarget.data;
    return useState(representedTarget)[disKey];
  };

  watch(
    () => originalData,
    () => getRepresentedTarget(),
    { deep: true, immediate: true }
  );

  return { representedTarget, targetMapping };
};

export const useMonitorType = () => {
  const monitorType = ref([
    {
      txt: $t('button.addEventPolicy'),
      iconType: 'line',
      iconName: 'policy_setting',
      chartType: 'line',
      type: 'policySetting'
    },
    {
      txt: $t('title.timeSeriesGraphAddWidget'),
      iconType: 'line',
      iconName: 'add',
      chartType: 'add',
      type: 'addWidget'
    }
  ]);
  return { monitorType };
};

export const useChartAction = () => {
  const chartStatus = ref({ zoom: false, legend: false });
  const actions = Object.keys(useState(chartStatus));

  const setAction = (action, value) => {
    chartStatus.value[action] = value;
  };

  const broadcastActions = (value, actionName = null) => {
    if (actionName) setAction(actionName, value);
    else for (const action of actions) setAction(action, value);
  };

  return { chartStatus, setAction, broadcastActions };
};
